import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  Chip,
  Paper,
  Stack,
  Avatar,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import {
  Search,
  School,
  CheckCircle,
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';

const LEVELS = ['本科', '硕士', '博士'];

const ProgramSelector = ({
  programs,
  selectedProgramId,
  onProgramSelect,
  onContinue,
  showContinueButton = true,
  variant = 'full', // 'full', 'compact', 'simple'
  onSearchChange,
  selectedLevel = '',
  onLevelSelect = () => {},
  searching = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [filteredPrograms, setFilteredPrograms] = useState(programs);

  const selectedProgram = programs.find(p => p.id === selectedProgramId);

  useEffect(() => {
    let filtered = programs;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(program =>
        (program.name || '').toLowerCase().includes(searchLower) ||
        (program.nameEn || '').toLowerCase().includes(searchLower) ||
        (program.category || '').toLowerCase().includes(searchLower) ||
        (program.university?.name || '').toLowerCase().includes(searchLower)
      );
    }

    if (categoryFilter) {
      filtered = filtered.filter(program => program.category === categoryFilter);
    }

    if (levelFilter) {
      filtered = filtered.filter(program => program.level === levelFilter);
    }

    setFilteredPrograms(filtered);
  }, [searchTerm, categoryFilter, levelFilter, programs]);

  const categories = [...new Set(programs.map(p => p.category))];

  const formatTuition = (tuition) => {
    if (!tuition || typeof tuition === 'string') {
      return tuition || '—';
    }
    if (tuition.international && typeof tuition.international === 'number') {
      return `RM ${tuition.international.toLocaleString()}`;
    }
    return '—';
  };

  const getLevelColor = (level) => {
    const colors = {
      '本科': 'success',
      '硕士': 'primary',
      '博士': 'secondary'
    };
    return colors[level] || 'default';
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    if (onSearchChange) {
      onSearchChange(event.target.value, selectedLevel);
    }
  };

  if (variant === 'simple') {
    return (
      <Box>
        <Autocomplete
          options={programs}
          getOptionLabel={(option) => `${option.name} - ${option.university.name}`}
          value={selectedProgram || null}
          onChange={(event, newValue) => onProgramSelect(newValue?.id || null)}
          loading={searching}
          renderInput={(params) => (
            <TextField
              {...params}
              label="选择专业"
              placeholder={searching ? "正在搜索..." : "搜索专业名称或大学..."}
              variant="outlined"
              fullWidth
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {searching ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => {
            const { key, ...otherProps } = props;
            return (
              <Box component="li" key={key} {...otherProps}>
                <Avatar
                  src={option.university.logo}
                  sx={{ width: 24, height: 24, mr: 2 }}
                >
                  {option.university.name.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {option.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.university.name} • {option.level} • {option.duration}
                  </Typography>
                </Box>
              </Box>
            );
          }}
        />
      </Box>
    );
  }

  if (variant === 'compact') {
    // 后端已做筛选，前端不再筛选
    const filteredCompactPrograms = programs;

    const LevelSelector = () => (
      <Box sx={{ mb: 2 }}>
        <FormControl fullWidth>
          <InputLabel>请选择学位</InputLabel>
          <Select
            value={selectedLevel}
            label="请选择学位"
            onChange={e => {
              const newLevel = e.target.value;
              onLevelSelect(newLevel);    // 通知外部
              setSearchTerm('');
              if (onSearchChange) onSearchChange('', newLevel);
              onProgramSelect(null); // 重置已选专业
            }}
          >
            <MenuItem value="">请选择学位</MenuItem>
            {LEVELS.map(level => (
              <MenuItem key={level} value={level}>{level}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );

    return (
      <Box>
        <LevelSelector />
        {selectedLevel && (
          <>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              选择专业
            </Typography>
            {selectedProgram && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'primary.main' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircle sx={{ color: 'success.main', mr: 2 }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {selectedProgram.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedProgram.university.name} • {selectedProgram.level} • {formatTuition(selectedProgram.tuitionFee)}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => onProgramSelect(null)}
                  >
                    更改
                  </Button>
                </Box>
              </Paper>
            )}
            {!selectedProgram && (
              <Autocomplete
                options={filteredCompactPrograms}
                getOptionLabel={(option) => `${option.name} - ${option.university.name}`}
                onChange={(event, newValue) => onProgramSelect(newValue?.id || null)}
                loading={searching}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="搜索专业"
                    placeholder={searching ? "正在搜索..." : "输入专业名称或大学..."}
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />,
                      endAdornment: (
                        <>
                          {searching ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    onChange={handleSearchChange}
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  return (
                    <Box component="li" key={key} {...otherProps}>
                      <Avatar
                        src={option.university.logo}
                        sx={{ width: 32, height: 32, mr: 2 }}
                      >
                        {option.university.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {option.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.university.name} • {option.level} • {option.duration}
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip 
                            label={option.category} 
                            size="small" 
                            variant="outlined"
                            sx={{ mr: 1 }}
                          />
                          <Chip 
                            label={formatTuition(option.tuitionFee)} 
                            size="small" 
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    </Box>
                  );
                }}
              />
            )}
            {showContinueButton && selectedProgram && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={onContinue}
                >
                  继续下一步
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    );
  }

  // （完整版未变）略...

  return (
    <Box>
      {/* 你可以继续保留完整版原逻辑 */}
      {/* 此处略去，为保持回复精简 */}
    </Box>
  );
};

export default ProgramSelector;
